/**
 * Password-reset REQUEST buttons — CU-86akkg3gu Part 1.
 *
 * ⚠️ THIS IS THE ONLY POINT IN THE EMAILED FLOW WE CAN WARN AT. The patient
 * completes the reset on a **Firebase-hosted page we do not control**
 * (`callbackUri` is the stock `/__/auth/action` — verified 2026-09-22 on both
 * projects). Nothing we ship reaches that page.
 *
 * ⚠️ THE COPY CANNOT BE FACTOR-AWARE HERE, and that is not an oversight. The
 * patient is NOT SIGNED IN — they are resetting precisely because they cannot
 * sign in — so we cannot read their passkeys or their SMS factor. The in-app
 * form (`PasswordUpdate.tsx`) tailors its warning because it can; this one must
 * claim nothing it cannot know.
 *
 * So the wording names the passkey consequence CONDITIONALLY ("any saved
 * passkeys") and says nothing whatsoever about text-message codes. Measured
 * (CU-86akjurnv Addendum 6): a password change reaps passkeys and SMS SURVIVES —
 * but asserting either to a patient whose factors we have not read would be a
 * guess printed as fact.
 */

import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../constants/firebaseModular';
import { Button } from '.';
import * as Sentry from '@sentry/browser';

export const RequestPasswordReset = (props: { email: string }) => {
  const onClick = () => {
    const validEmail = (email: string) => {
      return String(email)
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        );
    };

    if (!validEmail(props.email)) {
      alert('Please enter a valid email address');
      return;
    }
    sendPasswordResetEmail(auth, props.email)
      .then(() => {
        alert(
          'Password reset email sent.\n\n' +
            'After you set a new password, any saved passkeys on your account will stop working and you will need to add one again.'
        );
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('Error sending password reset email: ', error);
        alert('If an account exists with this email, a password reset link will be sent.');
      });
  };

  return <Button id="request-reset" type="button" textContent="Reset Password" onClick={onClick} />;
};

export const RequestSetPassword = (props: { email: string }) => {
  const onClick = () => {
    const validEmail = (email: string) => {
      return String(email)
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        );
    };

    if (!validEmail(props.email)) {
      alert('Please enter a valid email address');
      return;
    }
    sendPasswordResetEmail(auth, props.email)
      .then(() => {
        alert(
          'A link to set your password has been sent to your email.\n\n' +
            'After you set a new password, any saved passkeys on your account will stop working and you will need to add one again.'
        );
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('Error sending set password email: ', error);
        alert('If an account exists with this email, a link to set your password will be sent.');
      });
  };

  return <Button id="request-set-password" type="button" textContent="Set Password" onClick={onClick} />;
};
